import React, { useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { API, useAuth, apiPortraitUrl } from "@/auth";

/* ============================================================
 /studio — Character Studio (Champion / Battle Pass only)

 Pick attributes → Nano Banana generates a photorealistic
 hardcore rapper portrait. Locked behind a paid tier — no
 point building your character if you can't step in the arena.
 ============================================================ */

const PAID_TIERS = new Set(["battle_pass", "champion_pass"]);

const OPTIONS = {
 gender: [
 { v: "male", label: "MALE" },
 { v: "female", label: "FEMALE" },
 ],
 skin_tone: [
 { v: "light", label: "LIGHT" },
 { v: "olive", label: "OLIVE" },
 { v: "brown", label: "BROWN" },
 { v: "dark", label: "DARK" },
 ],
 build: [
 { v: "slim", label: "SLIM" },
 { v: "medium", label: "MEDIUM" },
 { v: "muscular", label: "MUSCULAR" },
 ],
 hair_male: [
 { v: "buzz cut", label: "BUZZ" },
 { v: "short fade", label: "FADE" },
 { v: "dreadlocks", label: "DREADS" },
 { v: "long braids", label: "BRAIDS" },
 { v: "bald", label: "BALD" },
 { v: "curly afro", label: "AFRO" },
 { v: "man-bun ponytail", label: "MAN-BUN" },
 ],
 hair_female: [
 { v: "long straight jet black hair", label: "STRAIGHT" },
 { v: "long braids", label: "BRAIDS" },
 { v: "curly natural hair", label: "CURLY" },
 { v: "short bob cut", label: "BOB" },
 { v: "long dreadlocks", label: "DREADS" },
 { v: "high ponytail", label: "PONYTAIL" },
 { v: "buzz cut with designs shaved", label: "BUZZ" },
 ],
 facial_hair: [
 { v: "clean shaven", label: "CLEAN" },
 { v: "short goatee", label: "GOATEE" },
 { v: "full beard", label: "FULL BEARD" },
 { v: "stubble", label: "STUBBLE" },
 ],
 top: [
 { v: "black hoodie", label: "HOODIE" },
 { v: "vintage leather jacket", label: "LEATHER" },
 { v: "oversized graphic tee", label: "GRAPHIC TEE" },
 { v: "vintage sports jersey", label: "JERSEY" },
 { v: "black bomber jacket", label: "BOMBER" },
 { v: "camo military jacket", label: "CAMO" },
 { v: "white tank top", label: "TANK" },
 ],
 accessories: [
 { v: "gold chain", label: "GOLD CHAIN" },
 { v: "iced-out diamond chain", label: "ICE CHAIN" },
 { v: "gold grill on teeth", label: "GRILL" },
 { v: "durag", label: "DURAG" },
 { v: "black bandana", label: "BANDANA" },
 { v: "backwards snapback cap", label: "SNAPBACK" },
 { v: "sunglasses", label: "SHADES" },
 { v: "diamond stud earrings", label: "STUDS" },
 { v: "hoop earrings", label: "HOOPS" },
 { v: "cross pendant", label: "CROSS" },
 ],
 tattoos: [
 { v: "no visible tattoos", label: "NONE" },
 { v: "few visible tattoos", label: "SUBTLE" },
 { v: "heavily tattooed arms and chest", label: "SLEEVES" },
 { v: "face and neck tattoos including teardrops", label: "FACE TATS" },
 ],
 mood: [
 { v: "confident intense stare", label: "CONFIDENT" },
 { v: "menacing snarl", label: "MENACING" },
 { v: "cocky smirk with head tilted back", label: "COCKY" },
 { v: "thoughtful contemplative", label: "PENSIVE" },
 { v: "mid-shout rap performance", label: "SHOUTING" },
 ],
};

function defaultAttrs(user) {
 return {
 gender: user?.gender || "male",
 skin_tone: "brown",
 build: "medium",
 hair: "buzz cut",
 facial_hair: "clean shaven",
 top: "black hoodie",
 accessories: ["gold chain"],
 tattoos: "few visible tattoos",
 mood: "confident intense stare",
 };
}

export default function Studio() {
 const { user, refresh } = useAuth();
 const nav = useNavigate();
 const [attrs, setAttrs] = useState(defaultAttrs(user));
 const [portrait, setPortrait] = useState(user?.portrait || null);
 const [busy, setBusy] = useState(false);
 const [err, setErr] = useState("");

 // Gate: must be logged in
 if (!user) {
 return (
 <div className="max-w-3xl mx-auto px-6 py-24 text-center">
 <div className="font-mono text-xs tracking-[0.4em] text-yt-red mb-4"> SIGN IN REQUIRED</div>
 <h1 className="font-display text-5xl md:text-6xl tracking-tight mb-6">
 BUILD YOUR <span className="text-yt-red">CHARACTER</span>
 </h1>
 <p className="text-white/70 font-mono text-sm max-w-lg mx-auto mb-8">
 Character Studio uses AI to generate your photorealistic rapper portrait — the face you&apos;ll wear in the cypher.
 </p>
 <Link to="/register" data-testid="studio-signup-cta"
 className="inline-block bg-yt-yellow text-black font-display text-xl px-8 py-4 tracking-wider shadow-brutal">
 CREATE ACCOUNT
 </Link>
 </div>
 );
 }

 const isPaid = user.role === "admin" || PAID_TIERS.has(user.tier);

 // Gate: must have a paid pass
 if (!isPaid) {
 return (
 <div className="max-w-4xl mx-auto px-6 py-20">
 <div className="border-4 border-yt-red bg-yt-surface shadow-brutal p-10 text-center">
 <div className="font-mono text-xs tracking-[0.4em] text-yt-red mb-4"> PAID FEATURE</div>
 <h1 className="font-display text-5xl md:text-7xl tracking-tight mb-6">
 CHARACTER <span className="text-yt-yellow">STUDIO</span>
 </h1>
 <p className="text-white/80 font-body text-lg max-w-2xl mx-auto mb-8">
 Design your own photorealistic hardcore rapper. Pick your skin, hair, chains,
 tats, and mood — AI renders a gritty street portrait that becomes your
 avatar in every battle.
 </p>
 <div className="bg-black/50 border-l-4 border-yt-yellow p-4 max-w-xl mx-auto mb-8 text-left">
 <div className="font-mono text-xs tracking-widest text-yt-yellow mb-2">WHY IT&apos;S PAID</div>
 <div className="font-mono text-sm text-white/80">
 What&apos;s the point of designing your character if you can&apos;t step in the arena?
 You need a pass to battle — grab one and Character Studio unlocks with it.
 </div>
 </div>
 <Link to="/subscribe" data-testid="studio-paywall-cta"
 className="inline-block bg-yt-yellow text-black font-display text-2xl tracking-wider px-10 py-5 shadow-brutal hover:bg-white transition-colors">
 ▶ GET A PASS · UNLOCK STUDIO
 </Link>
 <div className="font-mono text-xs text-yt-muted tracking-widest mt-6">
 ARENA + CHARTS PASS $4.99/mo · CHAMPION PASS $14.99/mo
 </div>
 <div className="font-mono text-[10px] text-yt-yellow/80 tracking-widest mt-2">
 ONE PASS · BATTLES + UNLIMITED SONG DROPS
 </div>
 </div>
 </div>
 );
 }

 const setAttr = (k, v) => setAttrs(a => ({ ...a, [k]: v }));

 const toggleAcc = (v) => {
 setAttrs(a => {
 const cur = new Set(a.accessories || []);
 if (cur.has(v)) cur.delete(v); else cur.add(v);
 return { ...a, accessories: Array.from(cur) };
 });
 };

 const generate = async () => {
 setBusy(true); setErr("");
 try {
 const { data } = await axios.post(`${API}/portraits/generate`, attrs);
 setPortrait(data.portrait);
 await refresh();
 } catch (e) {
 setErr(e.response?.data?.detail || "Portrait generation failed");
 } finally {
 setBusy(false);
 }
 };

 const hairOptions = attrs.gender === "female" ? OPTIONS.hair_female : OPTIONS.hair_male;

 return (
 <div className="max-w-7xl mx-auto px-6 md:px-10 py-12">
 <div className="font-mono text-xs tracking-[0.4em] text-yt-red mb-2"> CHARACTER STUDIO</div>
 <h1 className="font-display text-5xl md:text-7xl tracking-tight mb-2">
 BUILD YOUR <span className="text-yt-red">RAPPER</span>
 </h1>
 <p className="text-yt-muted font-mono text-xs tracking-widest mb-10 max-w-2xl">
 AI-GENERATED PHOTOREALISTIC PORTRAIT · SAVED TO YOUR PROFILE · REGENERATE ANYTIME
 </p>

 <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
 {/* LEFT — attribute pickers */}
 <div className="lg:col-span-3 space-y-6">
 <PickerRow label="GENDER" testKey="gender" options={OPTIONS.gender}
 value={attrs.gender} onChange={v => setAttr("gender", v)} />
 <PickerRow label="SKIN TONE" testKey="skin" options={OPTIONS.skin_tone}
 value={attrs.skin_tone} onChange={v => setAttr("skin_tone", v)} />
 <PickerRow label="BUILD" testKey="build" options={OPTIONS.build}
 value={attrs.build} onChange={v => setAttr("build", v)} />
 <PickerRow label="HAIR" testKey="hair" options={hairOptions}
 value={attrs.hair} onChange={v => setAttr("hair", v)} />
 {attrs.gender === "male" && (
 <PickerRow label="FACIAL HAIR" testKey="facial" options={OPTIONS.facial_hair}
 value={attrs.facial_hair} onChange={v => setAttr("facial_hair", v)} />
 )}
 <PickerRow label="TOP" testKey="top" options={OPTIONS.top}
 value={attrs.top} onChange={v => setAttr("top", v)} />

 <div>
 <div className="font-mono text-[10px] tracking-widest text-yt-muted mb-2">ACCESSORIES (multi)</div>
 <div className="flex flex-wrap gap-2">
 {OPTIONS.accessories.map(o => {
 const on = attrs.accessories?.includes(o.v);
 return (
 <button key={o.v} data-testid={`studio-acc-${o.v.replace(/\s/g, '_')}`}
 onClick={() => toggleAcc(o.v)}
 className={`px-3 py-2 text-xs font-mono tracking-widest border-2 transition-colors
 ${on ? "border-yt-yellow bg-yt-yellow text-black" : "border-yt-border text-white hover:border-yt-yellow"}`}>
 {o.label}
 </button>
 );
 })}
 </div>
 </div>

 <PickerRow label="TATTOOS" testKey="tats" options={OPTIONS.tattoos}
 value={attrs.tattoos} onChange={v => setAttr("tattoos", v)} />
 <PickerRow label="MOOD" testKey="mood" options={OPTIONS.mood}
 value={attrs.mood} onChange={v => setAttr("mood", v)} />

 {err && <div data-testid="studio-err" className="border border-yt-red/40 text-yt-red font-mono text-xs p-3">{err}</div>}

 <button data-testid="studio-generate-btn" onClick={generate} disabled={busy}
 className="w-full bg-yt-red text-white font-display text-2xl tracking-wider py-5 border-4 border-yt-yellow shadow-brutal hover:bg-yt-yellow hover:text-black transition-colors disabled:opacity-50">
 {busy ? "GENERATING (10-20 SEC)..." : portrait ? "REGENERATE PORTRAIT" : "▶ GENERATE MY PORTRAIT"}
 </button>
 </div>

 {/* RIGHT — live preview */}
 <div className="lg:col-span-2">
 <div className="sticky top-24">
 <div className="font-mono text-[10px] tracking-widest text-yt-muted mb-2">YOUR PORTRAIT</div>
 <div data-testid="studio-portrait-preview"
 className="aspect-[3/4] bg-black border-4 border-yt-yellow shadow-brutal overflow-hidden relative">
 {portrait ? (
 <img src={apiPortraitUrl(portrait)} alt="your rapper" className="w-full h-full object-cover" />
 ) : (
 <div className="w-full h-full flex flex-col items-center justify-center p-6 text-center">
 <div className="font-display text-6xl text-yt-muted mb-4">?</div>
 <div className="font-mono text-xs tracking-widest text-yt-muted">
 PICK YOUR ATTRIBUTES<br/>THEN GENERATE
 </div>
 </div>
 )}
 {busy && (
 <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
 <div className="font-mono text-yt-yellow animate-pulse-red tracking-widest">
 RENDERING...
 </div>
 </div>
 )}
 </div>
 <div className="mt-4 border border-yt-border bg-yt-surface p-3 font-mono text-[10px] text-yt-muted tracking-widest">
 {user.rapper_name?.toUpperCase()} · {user.style?.toUpperCase()} · {user.tier?.toUpperCase()?.replace('_', ' ')}
 </div>
 {portrait && (
 <button data-testid="studio-back-to-battles" onClick={() => nav("/battles/new")}
 className="w-full mt-4 bg-yt-yellow text-black font-display text-xl tracking-wider py-3 shadow-brutal hover:bg-white transition-colors">
 ▶ STEP INTO THE ARENA
 </button>
 )}
 </div>
 </div>
 </div>
 </div>
 );
}

function PickerRow({ label, testKey, options, value, onChange }) {
 return (
 <div>
 <div className="font-mono text-[10px] tracking-widest text-yt-muted mb-2">{label}</div>
 <div className="flex flex-wrap gap-2">
 {options.map(o => (
 <button key={o.v} data-testid={`studio-${testKey}-${o.v.replace(/\s/g, '_')}`}
 onClick={() => onChange(o.v)}
 className={`px-3 py-2 text-xs font-mono tracking-widest border-2 transition-colors
 ${value === o.v ? "border-yt-yellow bg-yt-yellow text-black" : "border-yt-border text-white hover:border-yt-yellow"}`}>
 {o.label}
 </button>
 ))}
 </div>
 </div>
 );
}
